import type { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { SOCIAL_LINKS } from "@/components/home/socialLinks";
import { focusRing } from "@/components/home/styles";
import logoDark from "@/assets/logo-dark.webp";

type FooterLink = { label: string; to?: string; hash?: string; href?: string };

const linkClass = `inline-flex min-h-[44px] items-center text-pf-ink2 transition-colors hover:text-foreground hover:underline ${focusRing}`;

const Column = ({ title, links, onHome }: { title: string; links: FooterLink[]; onHome: boolean }) => (
  <div>
    <h3 className="mb-1.5 text-[13px] font-semibold uppercase tracking-[0.06em] text-foreground">{title}</h3>
    <ul className="m-0 list-none p-0">
      {links.map((link) => {
        let node: ReactNode;
        if (link.href) {
          const external = link.href.startsWith("http");
          node = (
            <a
              href={link.href}
              className={linkClass}
              target={external ? "_blank" : undefined}
              rel={external ? "noopener noreferrer" : undefined}
            >
              {link.label}
            </a>
          );
        } else if (link.hash) {
          // "#plans" on the home page, "/#plans" from every other page.
          node = onHome ? (
            <a href={`#${link.hash}`} className={linkClass}>
              {link.label}
            </a>
          ) : (
            <Link to={`/#${link.hash}`} className={linkClass}>
              {link.label}
            </Link>
          );
        } else {
          node = (
            <Link to={link.to ?? "/"} className={linkClass}>
              {link.label}
            </Link>
          );
        }
        return <li key={link.label}>{node}</li>;
      })}
    </ul>
  </div>
);

const Footer = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const onHome = pathname === "/";
  const currentYear = new Date().getFullYear();

  const columns: { title: string; links: FooterLink[] }[] = [
    {
      title: t("footer.product"),
      links: [
        { label: t("footer.features"), to: "/features" },
        { label: t("home.footer.howItWorks"), to: "/how-it-works" },
        { label: t("home.nav.plans"), hash: "plans" },
        { label: t("home.footer.security"), to: "/security" },
        { label: t("header.faq"), to: "/faq" },
      ],
    },
    {
      title: t("home.footer.learn"),
      links: [
        { label: t("footer.blog"), to: "/blog" },
        { label: t("home.nav.tools"), to: "/tools" },
        { label: t("home.footer.halal"), to: "/halal-investing" },
        { label: t("home.footer.retirement"), to: "/blog/how-much-to-retire-at-60" },
      ],
    },
    {
      title: t("footer.company"),
      links: [
        { label: t("home.footer.contact"), href: "mailto:contact@getpiefolio.com" },
        ...SOCIAL_LINKS.map((s) => ({ label: s.name, href: s.href })),
      ],
    },
    {
      title: t("footer.legal"),
      links: [
        { label: t("footer.privacyPolicy"), to: "/privacy" },
        { label: t("footer.termsOfService"), to: "/terms" },
        { label: t("home.footer.subscriptionTerms"), to: "/subscription-terms" },
        { label: t("footer.cookiePolicy"), to: "/cookies" },
        { label: t("home.footer.deleteAccount"), to: "/delete-account" },
      ],
    },
  ];

  return (
    <footer className="bg-background px-[22px] pb-10 pt-14 text-sm leading-normal text-pf-ink2">
      <div className="mx-auto max-w-[1200px]">
        <div className="grid grid-cols-2 gap-8 md:grid-cols-4">
          {columns.map((col) => (
            <Column key={col.title} title={col.title} links={col.links} onHome={onHome} />
          ))}
        </div>

        <p className="mt-10 max-w-[760px] border-t border-foreground/10 pt-6 text-[13px]">{t("footer.disclaimer")}</p>

        <div className="mt-3 flex flex-col items-start justify-between gap-4 text-[13px] sm:flex-row sm:items-center">
          <span className="flex items-center gap-2">
            <img
              src={logoDark}
              alt="PieFolio AI"
              width="90"
              height="125"
              loading="lazy"
              decoding="async"
              className="block h-[18px] w-auto"
            />
            <span className="tabular-nums">{t("footer.copyright", { year: currentYear })}</span>
          </span>
          <LanguageSwitcher />
        </div>
      </div>
    </footer>
  );
};

export default Footer;
